import type { Application } from '~/generated/api-clients-generated'
import type { ComponentContent } from '~/types/cms'
import { Cms } from '~/composables/Cms'

export type ApplicationStatusType =
  | 'in-progress'
  | 'received'
  | 'pending'
  | 'approved'
  | 'denied'
  | 'contact-cs'

export type ApplicationStatus = {
  id: string
  display: string
  type: ApplicationStatusType
}

type ActiveRawStatus =
  | 'started'
  | 'submitted'
  | 'delivery_failed'
  | 'sent_to_carrier'
  | 'pending'
  | 'approved'
  | 'declined'
  | 'terminated'
  | 'request_for_information'

// Statuses filtered out entirely by isHidden before mapStatusType ever runs - not part of
// ApplicationStatusType, so they're intentionally excluded from the map below.
const hiddenStatuses = ['inactive_not_interested', 'inactive_backoffice', 'duplicate', 'cancelled']

const statusTypeMap: Record<ActiveRawStatus, ApplicationStatusType> = {
  started: 'in-progress',

  submitted: 'received',
  delivery_failed: 'received',

  sent_to_carrier: 'pending',
  pending: 'pending',

  approved: 'approved',

  declined: 'denied',

  // Deliberately 'contact-cs' rather than a distinct bucket - a terminated or
  // request-for-information application should route the customer to CS rather than to a
  // self-serve "find plans" flow. Each still gets its own body copy via
  // DashboardApplicationCard.vue's app_${status.id} CMS lookup (Directus, PageApplications[Dashboard]).
  terminated: 'contact-cs',
  request_for_information: 'contact-cs'
}

const mapStatusType = (app: Application): ApplicationStatusType => {
  if (!app?.status) {
    console.error('Failed to determine status group for app: ', app)
    return 'contact-cs'
  }

  const type = statusTypeMap[app.status as ActiveRawStatus]
  if (!type) {
    console.error('Unrecognized application status, defaulting to contact-cs: ', app.status)
    return 'contact-cs'
  }

  return type
}

export class ApplicationStatuses {
  static cmsContent: ComponentContent | null = null

  static async initialize() {
    const { getComponentContent } = Cms.use()
    const { content } = await getComponentContent('ApplicationStatusDisplay')

    this.cmsContent = content.value
  }

  static use() {
    const isInProgress = (app: Application) => mapStatusType(app) === 'in-progress'
    const isReceived = (app: Application) => mapStatusType(app) === 'received'
    const isPending = (app: Application) => mapStatusType(app) === 'pending'
    const isApproved = (app: Application) => mapStatusType(app) === 'approved'
    const isDenied = (app: Application) => mapStatusType(app) === 'denied'
    const isHidden = (app: Application) => app?.status && hiddenStatuses.includes(app.status)

    const getStatus = (app: Application): ApplicationStatus | null => {
      if (!this.cmsContent) throw new Error('CMS Content must be initialized')
      if (!app?.status) {
        console.error('Failed to determine status for application: ', app)
        return null
      }

      const display = this.cmsContent.ext?.[app.status]
      if (!display) {
        console.error('No CMS display label for application status: ', app.status)
      }

      return {
        id: app.status,
        display: display ?? app.status,
        type: mapStatusType(app)
      }
    }

    return {
      isInProgress,
      isReceived,
      isPending,
      isApproved,
      isDenied,
      isHidden,
      mapStatusType,
      getStatus
    }
  }
}
